// REPLACE: src/subscription.js
//
// Lean version. Every authenticated user gets 10 AI chats per calendar month,
// enforced server-side. This module:
//   - Exposes the limit constant (MONTHLY_CHAT_LIMIT)
//   - Reads the counter directly from Firestore (users/{uid}) via getChatUsage()
//   - Provides backward-compat stubs so the existing UI screens (ModePage,
//     SettingsPage, ProPage, FlashCardsPage, PaywallPopup) don't explode.
//     All feature gates (canUseAiChat / canUseTests / canUseFlashCards) now
//     return { allowed: true } — the server is the source of truth for chat,
//     and every other feature is unrestricted.
//
// Secret codes, tier switching, and local-storage counting are gone.

import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

export const MONTHLY_CHAT_LIMIT = 10;

function currentMonth() {
  // ISO year-month, e.g. "2026-04"
  return new Date().toISOString().slice(0, 7);
}

/**
 * Read the signed-in user's current-month chat usage from Firestore.
 * Returns { used, limit, remaining }. Safe to call when unauthenticated
 * (returns zeros). Never throws.
 */
export async function getChatUsage() {
  const user = auth.currentUser;
  if (!user) {
    return { used: 0, limit: MONTHLY_CHAT_LIMIT, remaining: MONTHLY_CHAT_LIMIT };
  }
  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    const data = snap.exists() ? snap.data() : {};
    const month = currentMonth();
    const used = data.chatMonth === month ? (data.chatCount || 0) : 0;
    return {
      used,
      limit: MONTHLY_CHAT_LIMIT,
      remaining: Math.max(0, MONTHLY_CHAT_LIMIT - used),
    };
  } catch {
    return { used: 0, limit: MONTHLY_CHAT_LIMIT, remaining: MONTHLY_CHAT_LIMIT };
  }
}

// ── Backward-compat stubs ────────────────────────────────────────────────────
// Existing UIs import these names. Kept as no-op allow-alls so screens render.
// Server enforces the real limit on /api/chat; other features are unrestricted.

export function canUseAiChat() {
  // Client no longer pre-gates chat — the server returns 429 when monthly
  // limit is reached, and the UI handles that with a "limit reached" message.
  return { allowed: true };
}

export function canUseTests() {
  return { allowed: true };
}

export function canUsePractice() {
  return { allowed: true };
}

export function canUseFlashCards() {
  return { allowed: true };
}

// Old "tier" model is gone. All users are on a single plan. Keep the shape
// minimal so imports like `TIERS[tier].name` still compile.
const DEFAULT_TIER = {
  name: "Samajh",
  label: `${MONTHLY_CHAT_LIMIT} chats / month`,
  price: 0,
  limits: { aiChat: MONTHLY_CHAT_LIMIT },
  features: { aiChat: true },
};
export const TIERS = {
  free: DEFAULT_TIER,
  // aliases for any old UI code that switches on "lite"/"pro"
  lite: DEFAULT_TIER,
  pro: DEFAULT_TIER,
};

export function getCurrentTier() { return "free"; }
export function getTierInfo() { return DEFAULT_TIER; }
export function getCurrentTierInfo() { return DEFAULT_TIER; }
export function setTier() { return false; }

// Codes no longer unlock anything. Surface a friendly message so the old
// "Apply code" UI doesn't silently pretend to work.
export function activateCode() {
  return { success: false, tier: null, message: "Codes are no longer supported." };
}

/**
 * Kept for the Settings page usage row. Used to read from localStorage;
 * now a sync stub that returns zeros. Components that need live numbers
 * should call the async getChatUsage() and hold state themselves.
 */
export function getUsageStats() {
  return {
    tier: "free",
    tierName: DEFAULT_TIER.name,
    aiChat: { used: 0, limit: MONTHLY_CHAT_LIMIT, remaining: MONTHLY_CHAT_LIMIT },
    practice: { used: 0, limit: 999, remaining: 999 },
    tests: { used: 0, limit: 999 },
  };
}

// Record stubs — kept so callers don't crash, but recording happens server-side now.
export function recordAiChat() { /* server enforces */ }
export function recordPractice() { /* no-op */ }
export function recordTest() { /* no-op */ }
